import ko from 'knockout';
import FirestoreClient from '../clients/FirestoreClient.js';
import { PLACEHOLDERS } from '../constants/index.js';
import { detectRegion } from '../utils/regionDetection.js';
import { normalizeProductTypes } from '../utils/normalization.js';
import { withTimeout } from '../utils/withTimeout.js';

class ProductRailViewModel {
    constructor(currentLangObservable) {
        this.currentLang = currentLangObservable;

        // Array of product rail items
        this.railProducts = ko.observableArray([]);
        this.currentProductId = ko.observable(null);
        this.isRailVisible = ko.observable(false);
        this.isInGuideBook = ko.observable(false);
        
        // Error state for rail load failures
        this.railLoadError = ko.observable(null);
        
        // Product type filter for header rail
        this.selectedProductType = null;

        // Subscriptions
        this.currentLang.subscribe(() => {
            this.loadRailProducts();
        });
    }
    
    /**
     * Initialize selected product type observable subscription
     * Called by AppViewModel to link rail filtering to main product type selector
     * @param {KO Observable} selectedProductTypeObservable
     */
    setSelectedProductType = (selectedProductTypeObservable) => {
        this.selectedProductType = selectedProductTypeObservable;
        
        // Issue 3.3: Debounce filter changes to prevent network spam
        // Delays loadRailProducts by 300ms when filter changes rapidly
        // Prevents multiple Firestore queries when user clicks filter options quickly
        let filterChangeTimeout = null;
        
        this.selectedProductType.subscribe(() => {
            clearTimeout(filterChangeTimeout);
            filterChangeTimeout = setTimeout(() => {
                this.loadRailProducts();
            }, 300);
        });
        // Initial load with current type
        this.loadRailProducts();
    };

    /**
     * Issue 1.3: Get current region using shared utility
     * Unified region detection across ViewModels (was using .includes() - now fixed)
     * @returns {string} Region code: 'pl', 'eu', or 'en'
     */
    #getCurrentRegion() {
        return detectRegion();
    }

    /**
     * Issue 3.1: Cache for rail products to avoid duplicate queries
     * Maps (region-lang-type) combinations to avoid re-querying Firestore
     */
    #cachedRailProducts = null;
    #lastRegion = null;
    #lastLang = null;
    #lastCacheKey = null;

    loadRailProducts = async () => {
        try {
            const lang = this.currentLang();
            const region = this.#getCurrentRegion();
            const selectedType = this.selectedProductType?.();
            
            // Issue 3.1: Check cache before querying
            // Creates cache key from region-lang-type combination
            const cacheKey = `${region}-${lang}-${selectedType || 'all'}`;
            
            // Use cached results if available with same parameters
            if (this.#cachedRailProducts && 
                this.#lastRegion === region && 
                this.#lastLang === lang &&
                this.#lastCacheKey === cacheKey) {
                // Apply cached rail items immediately
                this.railProducts(this.#cachedRailProducts);
                return;
            }
            
            // Issue 4.1: Wrap Firestore query with timeout protection
            const products = await withTimeout(
                FirestoreClient.queryCollection("products", {
                    whereField: "isActive",
                    whereOp: "==",
                    whereValue: true,
                    orderByField: "displayOrder",
                    order: "asc"
                }),
                10000,
                'Rail products loading'
            );

            // Filter products by region availability
            let filteredProducts = products.filter(p => p.regions?.includes(region));
            
            // NEW: Filter by selected product type if one is selected
            if (selectedType) {
                filteredProducts = filteredProducts.filter(p => {
                    const productTypes = normalizeProductTypes(p);  // Issue 2.2: Use shared utility
                    return productTypes.includes(selectedType);
                });
            }
            
            const railItems = filteredProducts.map(productData => {
                let displayName = productData.name;
                let displayThumbnail = productData.thumbnailImage || PLACEHOLDERS.PRODUCT_THUMBNAIL_SMALL;
                
                // Ensure image path is absolute
                if (displayThumbnail && !displayThumbnail.startsWith('http') && !displayThumbnail.startsWith('/')) {
                    displayThumbnail = '/' + displayThumbnail;
                }
                
                if (productData.content && productData.content[lang]) {
                    displayName = productData.content[lang].name || displayName;
                }

                return {
                    id: productData.id,
                    slug: productData.slug,
                    name: displayName,
                    imageSrc: displayThumbnail,
                    targetHash: `product/${productData.slug || productData.id}`,
                    productType: normalizeProductTypes(productData)[0] || 'mobile', // Get first product type for styling
                    // Issue 3.2: Removed computed properties - styling now in CSS
                    // isActive is bound to data-product-active attribute in template
                    isActive: ko.computed(() => this.currentProductId() === productData.id),
                    handleClick: () => this.#handleRailItemClick(productData.id, `product/${productData.slug || productData.id}`)
                };
            });

            // Issue 3.1: Update cache after successful load
            this.#cachedRailProducts = railItems;
            this.#lastRegion = region;
            this.#lastLang = lang;
            this.#lastCacheKey = cacheKey;
            
            this.railProducts(railItems);
            this.railLoadError(null);  // Clear error on successful load
        } catch (error) {
            console.error('Error loading rail products:', error);
            this.railProducts([]);
            // Set error state with retry function for user recovery
            this.railLoadError({
                message: error.message || 'Failed to load products. Please check your connection.',
                retry: () => this.loadRailProducts()
            });
        }
    };



    /**
     * Handle click on a rail item
     * Navigates to product if not active, or if in guide mode
     * @private
     * @param {string} productId - Product ID
     * @param {string} targetHash - Target product hash
     */
    #handleRailItemClick(productId, targetHash) {
        const isActive = this.currentProductId() === productId;
        if (!isActive || this.isInGuideBook()) {
            this.navigateToProduct(targetHash.replace('product/', ''));
        }
        return false;
    }

    setCurrentProduct = (productId) => {
        this.currentProductId(productId);
        this.showRail();
    };

    showRail = () => {
        this.isRailVisible(true);
    };

    hideRail = () => {
        this.isRailVisible(false);
    };

    scrollRailToActive = () => {
        setTimeout(() => {
            const container = document.getElementById('product-rail-container');
            const activeItem = document.querySelector('[data-product-active="true"]');
            if (container && activeItem) {
                const containerRect = container.getBoundingClientRect();
                const itemRect = activeItem.getBoundingClientRect();
                const scrollAmount = itemRect.left - containerRect.left - (containerRect.width / 2) + (itemRect.width / 2);
                container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
            }
        }, 50);
    };

    navigateToProduct = (productSlugOrId) => {
        if (window.Engryv && window.Engryv.appViewModel && typeof window.Engryv.navigation && typeof window.Engryv.navigation.cinematicProductNav === 'function') {
            window.Engryv.navigation.cinematicProductNav('product/' + productSlugOrId);
        }
    };

    navigateToGuidebook = (productIdOrSlug) => {
        if (window.Engryv && window.Engryv.appViewModel && typeof window.Engryv.navigation && typeof window.Engryv.navigation.cinematicProductNav === 'function') {
            // Find the product slug from railProducts if only ID was provided
            let productSlug = productIdOrSlug;
            const product = this.railProducts().find(p => p.id === productIdOrSlug);
            if (product && product.slug) {
                productSlug = product.slug;
            }
            window.Engryv.navigation.cinematicProductNav('product/' + productSlug + '/guidebook');
        }
    };

    setGuideBookView = (isInGuideBook) => {
        this.isInGuideBook(isInGuideBook);
    };
}

export default ProductRailViewModel;
